import type { ComponentProps } from 'react';
import React, { useCallback } from 'react';
import { useRouter } from 'expo-router';
import { defineMessages, useIntl } from 'react-intl';

import { useFeatureFlag } from '@order/LaunchDarkly';

import { LocationSearch, NotFoundView, TransparentModal } from '../../components';
import { useLastInteractedStoreMachine } from '../../context';
import { useNavigateBack } from '../../navigation';

// ─────────────────────────────────────────────────────────────────────────────

export const BundleLocationScreen = () => {
  const isBundleEnabled = useFeatureFlag('CELS-5880-order-product-bundles-enabled');
  const navigateBack = useNavigateBack();
  const { formatMessage } = useIntl();

  if (!isBundleEnabled) {
    return (
      <TransparentModal
        accessibilityLabel={formatMessage(messages.dialogA11yLabel)}
        size="large"
        offset="10%"
        onClose={navigateBack}
      >
        <NotFoundView withoutFooter />
      </TransparentModal>
    );
  }

  return <BundleLocationScreenReady />;
};

// ─────────────────────────────────────────────────────────────────────────────

const BundleLocationScreenReady = () => {
  const router = useRouter();
  const { formatMessage } = useIntl();
  const navigateBack = useNavigateBack();

  const { send: sendToLastInteractedStoreMachine } = useLastInteractedStoreMachine();

  // ─── Handlers ────────────────────────────────────────────────────────

  const navigateToLocation = useCallback<OnOpenRestaurant>(
    (context) => {
      if (!context.activeLocation) return;

      const { restaurantId, restaurantName, restaurantSlug } = context.activeLocation;

      sendToLastInteractedStoreMachine({ type: 'RESET_ACTIVE_CART_STORE' });
      sendToLastInteractedStoreMachine({
        type: 'SET_LAST_INTERACTED_MENU_STORE',
        store: { restaurantId, restaurantName, restaurantSlug },
      });

      router.back();
    },
    [router, sendToLastInteractedStoreMachine],
  );

  const navigateToDeliveryLocation = useCallback<OnOpenDeliveryRestaurant>(
    (context) => {
      const { deliveryLocation } = context;

      if (!deliveryLocation) return;

      const {
        addressId,
        restaurantId,
        restaurantName,
        restaurantSlug,
        restaurantDeliveryFee,
        deliveryVendorName,
        deliveryVendorRestaurantId,
      } = deliveryLocation;

      sendToLastInteractedStoreMachine({ type: 'RESET_ACTIVE_CART_STORE' });
      sendToLastInteractedStoreMachine({
        type: 'SET_LAST_INTERACTED_MENU_STORE',
        store: {
          restaurantId,
          restaurantName,
          restaurantSlug,
          addressId,
          deliveryFee: restaurantDeliveryFee,
          vendor: deliveryVendorName,
          vendorRestaurantId: deliveryVendorRestaurantId,
        },
      });

      router.back();
    },
    [router, sendToLastInteractedStoreMachine],
  );

  // ─────────────────────────────────────────────────────────────────────

  return (
    <TransparentModal
      accessibilityLabel={formatMessage(messages.dialogA11yLabel)}
      size="large"
      offset="10%"
      onClose={navigateBack}
    >
      <LocationSearch
        navigateToLocation={navigateToLocation}
        navigateToDeliveryLocation={navigateToDeliveryLocation}
        interactedLocationRestaurantSlug={undefined}
        interactedLocationDeliveryAddressId={undefined}
        isInsideModal
      />
    </TransparentModal>
  );
};

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  dialogA11yLabel: {
    defaultMessage: 'Select a location',
    description: 'Bundle | Location search | Dialog a11y label',
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type LocationSearchProps = ComponentProps<typeof LocationSearch>;

type OnOpenRestaurant = NonNullable<LocationSearchProps['navigateToLocation']>;
type OnOpenDeliveryRestaurant = NonNullable<LocationSearchProps['navigateToDeliveryLocation']>;
